import * as THREE from 'three';
import { ItemBoxState, ProjectileState, BananaState, CoinState, ItemType } from '@shared/types';

interface ItemBoxVisual {
  group: THREE.Group;
  outerBox: THREE.Mesh;
  innerBox: THREE.Mesh;
  glow: THREE.Mesh;
  baseY: number;
}

export class ItemRenderer {
  private itemBoxes: ItemBoxVisual[] = [];
  private projectileMeshes: Map<string, THREE.Group> = new Map();
  private bananaMeshes: Map<string, THREE.Group> = new Map();
  private coinMeshes: Map<number, THREE.Group> = new Map();

  // Shared materials for projectiles (reused)
  private projectileMat: THREE.MeshStandardMaterial;
  private projectileGlowMat: THREE.MeshStandardMaterial;

  // Shared shell materials
  private greenShellMat: THREE.MeshStandardMaterial;
  private redShellMat: THREE.MeshStandardMaterial;
  private blueShellMat: THREE.MeshStandardMaterial;
  private shellBottomMat: THREE.MeshStandardMaterial;

  // Shared banana material
  private bananaMat: THREE.MeshStandardMaterial;

  // Shared coin material
  private coinMat: THREE.MeshStandardMaterial;

  // Shared geometries
  private shellGeo: THREE.SphereGeometry;
  private shellDiscGeo: THREE.CircleGeometry;
  private bananaGeo: THREE.CylinderGeometry;
  private coinGeo: THREE.CylinderGeometry;

  constructor(private scene: THREE.Scene) {
    this.projectileMat = new THREE.MeshStandardMaterial({
      color: 0xff4444,
      emissive: 0xff0000,
      emissiveIntensity: 0.8,
      roughness: 0.3,
      metalness: 0.4,
    });
    this.projectileGlowMat = new THREE.MeshStandardMaterial({
      color: 0xff6644,
      emissive: 0xff4422,
      emissiveIntensity: 1.2,
      transparent: true,
      opacity: 0.4,
      roughness: 0.0,
      metalness: 0.0,
    });

    // Shell materials
    this.greenShellMat = new THREE.MeshStandardMaterial({
      color: 0x22cc44,
      emissive: 0x118822,
      emissiveIntensity: 0.3,
      roughness: 0.4,
      metalness: 0.2,
    });
    this.redShellMat = new THREE.MeshStandardMaterial({
      color: 0xcc2222,
      emissive: 0x881111,
      emissiveIntensity: 0.3,
      roughness: 0.4,
      metalness: 0.2,
    });
    this.blueShellMat = new THREE.MeshStandardMaterial({
      color: 0x2244cc,
      emissive: 0x112288,
      emissiveIntensity: 0.5,
      roughness: 0.3,
      metalness: 0.3,
    });
    this.shellBottomMat = new THREE.MeshStandardMaterial({
      color: 0xeeeecc,
      roughness: 0.6,
      metalness: 0.1,
    });

    // Banana material
    this.bananaMat = new THREE.MeshStandardMaterial({
      color: 0xddcc22,
      emissive: 0x554400,
      emissiveIntensity: 0.2,
      roughness: 0.5,
      metalness: 0.1,
    });

    // Coin material — high metalness and emissive for bloom
    this.coinMat = new THREE.MeshStandardMaterial({
      color: 0xffdd44,
      emissive: 0xffaa00,
      emissiveIntensity: 1.5,   // Increased for good bloom
      roughness: 0.1,
      metalness: 0.95,
      envMapIntensity: 2.0,
    });

    // Shared geometries
    this.shellGeo = new THREE.SphereGeometry(0.4, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
    this.shellDiscGeo = new THREE.CircleGeometry(0.4, 12);
    this.bananaGeo = new THREE.CylinderGeometry(0.15, 0.2, 0.5, 8);
    this.coinGeo = new THREE.CylinderGeometry(0.4, 0.4, 0.06, 16);
  }

  // ────────────────────────────────────────────────────────────
  // Shell mesh creation
  // ────────────────────────────────────────────────────────────

  private createGreenShell(): THREE.Group {
    const group = new THREE.Group();

    // Half-sphere top
    const top = new THREE.Mesh(this.shellGeo, this.greenShellMat);
    top.castShadow = true;
    group.add(top);

    // Flat bottom disc
    const disc = new THREE.Mesh(this.shellDiscGeo, this.shellBottomMat);
    disc.rotation.x = -Math.PI / 2;
    group.add(disc);

    return group;
  }

  private createRedShell(): THREE.Group {
    const group = new THREE.Group();

    const top = new THREE.Mesh(this.shellGeo, this.redShellMat);
    top.castShadow = true;
    group.add(top);

    const disc = new THREE.Mesh(this.shellDiscGeo, this.shellBottomMat);
    disc.rotation.x = -Math.PI / 2;
    group.add(disc);

    return group;
  }

  private createBlueShell(): THREE.Group {
    const group = new THREE.Group();

    // Half-sphere top
    const top = new THREE.Mesh(this.shellGeo, this.blueShellMat);
    top.castShadow = true;
    group.add(top);

    const disc = new THREE.Mesh(this.shellDiscGeo, this.shellBottomMat);
    disc.rotation.x = -Math.PI / 2;
    group.add(disc);

    // Wing planes on sides
    const wingGeo = new THREE.PlaneGeometry(0.6, 0.3);
    const wingMat = new THREE.MeshStandardMaterial({
      color: 0x4466ee,
      emissive: 0x2244cc,
      emissiveIntensity: 0.4,
      side: THREE.DoubleSide,
    });

    const leftWing = new THREE.Mesh(wingGeo, wingMat);
    leftWing.position.set(-0.5, 0.15, 0);
    leftWing.rotation.z = 0.3;
    group.add(leftWing);

    const rightWing = new THREE.Mesh(wingGeo, wingMat);
    rightWing.position.set(0.5, 0.15, 0);
    rightWing.rotation.z = -0.3;
    group.add(rightWing);

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Banana mesh creation
  // ────────────────────────────────────────────────────────────

  private createBanana(): THREE.Group {
    const group = new THREE.Group();

    // Create a curved banana using two cylinders at slight angles
    const seg1 = new THREE.Mesh(this.bananaGeo, this.bananaMat);
    seg1.position.set(-0.1, 0.15, 0);
    seg1.rotation.z = 0.3;
    seg1.castShadow = true;
    group.add(seg1);

    const seg2 = new THREE.Mesh(this.bananaGeo, this.bananaMat);
    seg2.position.set(0.1, 0.15, 0);
    seg2.rotation.z = -0.3;
    seg2.castShadow = true;
    group.add(seg2);

    // Small brown tip
    const tipGeo = new THREE.SphereGeometry(0.06, 6, 4);
    const tipMat = new THREE.MeshStandardMaterial({ color: 0x553300, roughness: 0.8 });
    const tip = new THREE.Mesh(tipGeo, tipMat);
    tip.position.set(0, 0.42, 0);
    group.add(tip);

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Coin mesh creation
  // ────────────────────────────────────────────────────────────

  private createCoin(): THREE.Group {
    const group = new THREE.Group();

    const coin = new THREE.Mesh(this.coinGeo, this.coinMat);
    coin.castShadow = true;
    // Stand the coin upright (rotate so disc face is vertical)
    coin.rotation.x = Math.PI / 2;
    group.add(coin);

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Item boxes
  // ────────────────────────────────────────────────────────────

  /** Create item box meshes at given positions */
  createItemBoxes(positions: Array<{ x: number; z: number }>): void {
    this.clearItemBoxes();

    for (const pos of positions) {
      const group = new THREE.Group();

      // Outer box: metallic with emissive question mark glow
      const outerGeo = new THREE.BoxGeometry(1.5, 1.5, 1.5);
      const outerMat = new THREE.MeshStandardMaterial({
        color: 0xffdd00,
        emissive: 0xffaa00,
        emissiveIntensity: 0.8,
        roughness: 0.2,
        metalness: 0.6,
        transparent: true,
        opacity: 0.9,
        envMapIntensity: 1.5,
        wireframe: false,
      });
      const outerBox = new THREE.Mesh(outerGeo, outerMat);
      outerBox.castShadow = true;
      group.add(outerBox);

      // Inner box: rotated 45 degrees, smaller, different color for "?" illusion
      const innerGeo = new THREE.BoxGeometry(0.9, 0.9, 0.9);
      const innerMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffaa00,
        emissiveIntensity: 0.5,
        roughness: 0.2,
        metalness: 0.1,
      });
      const innerBox = new THREE.Mesh(innerGeo, innerMat);
      innerBox.rotation.y = Math.PI / 4;
      innerBox.rotation.x = Math.PI / 4;
      group.add(innerBox);

      // Inner glow sphere
      const glowGeo = new THREE.SphereGeometry(1.2, 12, 8);
      const glowMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffee88,
        emissiveIntensity: 2.0,
        transparent: true,
        opacity: 0.3,
        roughness: 0.0,
        metalness: 0.0,
        side: THREE.DoubleSide,
      });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      group.add(glow);

      // Diamond orientation (rotate 45 degrees on an axis)
      group.rotation.y = Math.PI / 4;

      const baseY = 1.8;
      group.position.set(pos.x, baseY, pos.z);

      this.scene.add(group);
      this.itemBoxes.push({
        group,
        outerBox,
        innerBox,
        glow,
        baseY,
      });
    }
  }

  /** Update item box visibility based on active state */
  updateItemBoxes(states: ItemBoxState[]): void {
    for (let i = 0; i < states.length && i < this.itemBoxes.length; i++) {
      const state = states[i];
      const visual = this.itemBoxes[i];
      visual.group.visible = state.active;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Projectiles
  // ────────────────────────────────────────────────────────────

  /** Update projectile positions */
  updateProjectiles(projectiles: ProjectileState[]): void {
    // Track which projectile IDs are still active
    const activeIds = new Set<string>();

    for (const proj of projectiles) {
      activeIds.add(proj.id);

      let visual = this.projectileMeshes.get(proj.id);
      if (!visual) {
        // Create appropriate mesh based on shell type
        visual = this.createProjectileByType(proj.shellType);
        this.scene.add(visual);
        this.projectileMeshes.set(proj.id, visual);
      }

      const yPos = proj.y !== undefined ? proj.y : 0.8;
      visual.position.set(proj.x, yPos, proj.z);

      // Orient projectile in its movement direction
      if (proj.vx !== 0 || proj.vz !== 0) {
        const angle = Math.atan2(proj.vz, proj.vx);
        visual.rotation.y = Math.PI / 2 - angle;
      }
    }

    // Remove projectiles that no longer exist
    for (const [id, visual] of this.projectileMeshes.entries()) {
      if (!activeIds.has(id)) {
        this.scene.remove(visual);
        this.disposeGroup(visual);
        this.projectileMeshes.delete(id);
      }
    }
  }

  private createProjectileByType(shellType: string): THREE.Group {
    switch (shellType) {
      case 'green':
        return this.createGreenShell();
      case 'red':
        return this.createRedShell();
      case 'blue':
        return this.createBlueShell();
      case 'legacy':
      default:
        return this.createLegacyProjectile();
    }
  }

  private createLegacyProjectile(): THREE.Group {
    const group = new THREE.Group();

    // Main sphere
    const sphereGeo = new THREE.SphereGeometry(0.5, 12, 8);
    const sphere = new THREE.Mesh(sphereGeo, this.projectileMat);
    sphere.castShadow = true;
    group.add(sphere);

    // Outer glow
    const glowGeo = new THREE.SphereGeometry(0.7, 10, 6);
    const glow = new THREE.Mesh(glowGeo, this.projectileGlowMat);
    group.add(glow);

    // Trailing spikes for a menacing look
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const spikeGeo = new THREE.ConeGeometry(0.1, 0.35, 4);
      const spikeMat = new THREE.MeshStandardMaterial({
        color: 0xff6644,
        emissive: 0xff4422,
        emissiveIntensity: 0.6,
      });
      const spike = new THREE.Mesh(spikeGeo, spikeMat);
      spike.position.set(
        Math.cos(angle) * 0.5,
        Math.sin(angle) * 0.5,
        0
      );
      // Point spike outward
      spike.rotation.z = angle - Math.PI / 2;
      group.add(spike);
    }

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Bananas
  // ────────────────────────────────────────────────────────────

  /** Update banana positions. Creates/removes banana meshes as needed. */
  updateBananas(bananas: BananaState[]): void {
    const activeIds = new Set<string>();

    for (const b of bananas) {
      activeIds.add(b.id);

      let visual = this.bananaMeshes.get(b.id);
      if (!visual) {
        visual = this.createBanana();
        this.scene.add(visual);
        this.bananaMeshes.set(b.id, visual);
      }

      visual.position.set(b.x, 0.0, b.z);
    }

    // Remove bananas that no longer exist
    for (const [id, visual] of this.bananaMeshes.entries()) {
      if (!activeIds.has(id)) {
        this.scene.remove(visual);
        this.disposeGroup(visual);
        this.bananaMeshes.delete(id);
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Coins
  // ────────────────────────────────────────────────────────────

  /** Update coin positions. Creates/removes/hides coin meshes. */
  updateCoins(coins: CoinState[]): void {
    const activeIndices = new Set<number>();

    for (const c of coins) {
      activeIndices.add(c.index);

      let visual = this.coinMeshes.get(c.index);
      if (!visual) {
        visual = this.createCoin();
        visual.position.set(c.x, 1.0, c.z);
        this.scene.add(visual);
        this.coinMeshes.set(c.index, visual);
      }

      // Show/hide based on active state
      visual.visible = c.active;
    }

    // Remove coin meshes for indices that are no longer in the state
    for (const [idx, visual] of this.coinMeshes.entries()) {
      if (!activeIndices.has(idx)) {
        this.scene.remove(visual);
        this.disposeGroup(visual);
        this.coinMeshes.delete(idx);
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Animation
  // ────────────────────────────────────────────────────────────

  /** Animate item boxes, projectiles, bananas, and coins (call each frame) */
  animate(time: number): void {
    const bobSpeed = 2.0;
    const bobHeight = 0.3;
    const rotSpeed = 1.5;

    for (const item of this.itemBoxes) {
      if (!item.group.visible) continue;

      // Bob up and down
      item.group.position.y = item.baseY + Math.sin(time * bobSpeed) * bobHeight;

      // Rotate slowly
      item.outerBox.rotation.y += 0.01 * rotSpeed;
      item.outerBox.rotation.x += 0.005 * rotSpeed;

      // Inner box rotates in the opposite direction
      item.innerBox.rotation.y -= 0.015 * rotSpeed;
      item.innerBox.rotation.z += 0.01 * rotSpeed;

      // Glow pulses
      const glowMat = item.glow.material as THREE.MeshStandardMaterial;
      glowMat.opacity = 0.1 + Math.sin(time * 3) * 0.08;
    }

    // Animate projectiles: spin them
    for (const [, visual] of this.projectileMeshes) {
      visual.children[0].rotation.y += 0.1;
      if (visual.children.length > 1) {
        visual.children[0].rotation.x += 0.05;
      }
    }

    // Animate coins: bob and rotate
    for (const [, visual] of this.coinMeshes) {
      if (!visual.visible) continue;
      // Bob up and down
      visual.position.y = 1.0 + Math.sin(time * 3.0) * 0.2;
      // Rotate
      visual.rotation.y += 0.03;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Cleanup
  // ────────────────────────────────────────────────────────────

  /** Clear only item boxes */
  private clearItemBoxes(): void {
    for (const item of this.itemBoxes) {
      this.scene.remove(item.group);
      this.disposeGroup(item.group);
    }
    this.itemBoxes = [];
  }

  /** Clear everything */
  clear(): void {
    this.clearItemBoxes();

    for (const [, visual] of this.projectileMeshes) {
      this.scene.remove(visual);
      this.disposeGroup(visual);
    }
    this.projectileMeshes.clear();

    for (const [, visual] of this.bananaMeshes) {
      this.scene.remove(visual);
      this.disposeGroup(visual);
    }
    this.bananaMeshes.clear();

    for (const [, visual] of this.coinMeshes) {
      this.scene.remove(visual);
      this.disposeGroup(visual);
    }
    this.coinMeshes.clear();
  }

  /** Full disposal including shared materials */
  dispose(): void {
    this.clear();
    this.projectileMat.dispose();
    this.projectileGlowMat.dispose();
    this.greenShellMat.dispose();
    this.redShellMat.dispose();
    this.blueShellMat.dispose();
    this.shellBottomMat.dispose();
    this.bananaMat.dispose();
    this.coinMat.dispose();
    this.shellGeo.dispose();
    this.shellDiscGeo.dispose();
    this.bananaGeo.dispose();
    this.coinGeo.dispose();
  }

  private disposeGroup(group: THREE.Object3D): void {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          // Don't dispose shared geometries
          if (
            child.geometry !== this.shellGeo &&
            child.geometry !== this.shellDiscGeo &&
            child.geometry !== this.bananaGeo &&
            child.geometry !== this.coinGeo
          ) {
            child.geometry.dispose();
          }
        }
        if (child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) {
            // Don't dispose shared materials
            if (
              m !== this.projectileMat &&
              m !== this.projectileGlowMat &&
              m !== this.greenShellMat &&
              m !== this.redShellMat &&
              m !== this.blueShellMat &&
              m !== this.shellBottomMat &&
              m !== this.bananaMat &&
              m !== this.coinMat
            ) {
              m.dispose();
            }
          }
        }
      }
    });
  }
}
